import { defineComponent as _defineComponent } from 'vue'
import { computed, provide } from 'vue';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { OfferLocation } from '@/helpers/offer-helper';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';

import { allowedPromotionLanguages, usePromotion } from '@/helpers/composables/usePromotion';
import type { BuyboxRegularOffer, UpcomingReleaseBuybox } from '@/features/buybox/types/types.ts';

import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { useOverProviderExp } from '@/components/experiments/OverProviderExp';
import { qtvProvidersByType } from '@/features/buybox/helpers/quality-tv-data';
import { useExperiment } from '@/libraries/experiment';
import { useJWTVCampaign } from '@/features/jwtv-titles-rental/composables/useJWTVCampaign';


export default /*#__PURE__*/_defineComponent({
  __name: 'RegularListBuyBox',
  props: {
    regularOffers: null,
    allOffers: null,
    filter: null,
    title: null,
    additionalContexts: null,
    upcomingReleases: null,
    isInWatchlist: { type: Boolean },
    seasonNumber: null,
    episodeNumber: null,
    isEpisodeBuybox: { type: Boolean },
    hasVODOffers: { type: Boolean }
  },
  emits: ["onNotifyMe"],
  setup(__props: any) {

const props = __props as {
	regularOffers: BuyboxRegularOffer[];
	allOffers: TitleOfferFragment[];
	filter: BuyBoxFilterOptions;
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
	upcomingReleases: UpcomingReleaseBuybox[];
	isInWatchlist: boolean;
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	isEpisodeBuybox?: boolean;
	hasVODOffers: boolean;
};

const ListUpcoming = () => import('@/features/buybox/components/list/ListUpcoming.vue');
const ListOffer = () => import('@/features/buybox/components/list/ListOffer.vue');
const NoOffers = () => import('@/features/buybox/components/list/NoOffers.vue');
const NotifyMe = () => import('@/features/buybox/components/list/NotifyMe.vue');
const PromotionPlacement = () => import('@/features/buybox/components/list/PromotionPlacement.vue');





const { language, country } = storeToRefs(useLanguageStore());
const { isLoggedIn } = storeToRefs(useUserStore());

provide('offerLocation', OfferLocation.REGULAR_BUYBOX);

const { promotionOffer, inPromotionCatalogue, catalogueItem } = usePromotion({
	title: () => props.title,
	offers: () => props.allOffers,
});

const { activeVariantWithControlGroup: buyRentExpVariant } = useExperiment('EXP_MERGE_BUY_RENT');
// EXP_PROMOTED_FREE_TRIAL_REDESIGN
const { activeVariantWithControlGroup: pftrVariant } = useExperiment('EXP_PROMOTED_FREE_TRIAL_REDESIGN');
// EXP_PROMOTED_FREE_TRIAL_REDESIGN

const showFreeTrial = computed(
	() =>
		!!promotionOffer.value &&
		[
			BuyBoxFilterOptions.ALL,
			BuyBoxFilterOptions.FREE,
			BuyBoxFilterOptions.SUBSCRIPTION,
			// EXP_PROVIDER_BUYBOX_V2: fall back to the Free Trial promo when the selected My Services providers have no offer
			BuyBoxFilterOptions.MY_SERVICES,
		].includes(props.filter) &&
		// splitting language for locale specific ones like Spanish - Spain, Spanish - Latin America, etc
		allowedPromotionLanguages.includes(language.value.split('-')[0])
);

const hasFreeTrialOffers = computed(() => props?.regularOffers.length > 0 || inPromotionCatalogue.value);

const showNoOffers = computed(() => props.regularOffers.length === 0);

const showNotifyMeNew = computed(() => {
	return props.filter === BuyBoxFilterOptions.FREE ? hasFreeTrialOffers.value : !showNoOffers.value;
});

const hasOnlyUpcomingOffers = computed(() => props.upcomingReleases.length > 0 && showNoOffers.value);

//  Quality TV
const qualityTvTrackingContexts = computed(() => {
	const titleObjectId = props.title.__typename === 'Season' ? props.title.show.objectId : props.title.objectId;
	const titleObjectType = props.title.__typename === 'Season' ? props.title.show.objectType : props.title.objectType;
	const titleContext = new SnowplowTitleContextGraphql(
		titleObjectId,
		titleObjectType,
		props.seasonNumber ?? null,
		props.episodeNumber ?? null
	);

	return (props.additionalContexts || []).concat([titleContext]);
});
// Quality TV

const isFreeTrialPositionFeature = computed(
	() => ['GB', 'CA', 'DE', 'MX', 'ES', 'IN', 'US', 'AU'].includes(country.value) && !isLoggedIn.value
);

const { isOverProviderTitle } = useOverProviderExp({ promotionOffer });

const includeTVODQTVonRentOrBuy = computed(() => {
	// Include TVOD QTV (which acts as a 'freeTrial' offer) also on Rent and Buy filters. Other free trial offers are not included.
	return (
		catalogueItem.value?.placement === 'qtv' &&
		qtvProvidersByType['TVOD'].includes(catalogueItem.value.shortName) &&
		[BuyBoxFilterOptions.BUY, BuyBoxFilterOptions.RENT].includes(props.filter)
	);
});

const { hasActiveJwtvCampaign } = useJWTVCampaign();

const _allOffers = computed<BuyboxRegularOffer[]>(() => {
	let offers: BuyboxRegularOffer[] = [...props.regularOffers];
	// Add promotion offer to the list of offers
	if (
		!showNoOffers.value &&
		([BuyBoxFilterOptions.ALL, BuyBoxFilterOptions.SUBSCRIPTION, BuyBoxFilterOptions.FREE].includes(props.filter) ||
			includeTVODQTVonRentOrBuy.value) &&
		!!promotionOffer.value
	) {
		// OVER_PROVIDER
		if (isOverProviderTitle.value) {
			offers = [
				{ ...promotionOffer.value, rowType: 'freeTrial' } as BuyboxRegularOffer,
				// and we filter out the offers that are of the same provider as the free trial
				...offers.filter(offer => offer.package.shortName !== promotionOffer.value?.package.shortName),
			];
		} else {
			const isSeasonPage = props.title.__typename === 'Season';
			let rowPosition = isFreeTrialPositionFeature.value && isSeasonPage ? 3 : 2;
			// EXP_PROMOTED_FREE_TRIAL_REDESIGN: variant_4 moves the fallback free trial row ("watch similar
			// movies free" - no real offer for this title) to pos 4 (index 3) instead of pos 3, once there
			// are enough offers to fill that slot. Real, non-fallback free trial offers are unaffected.
			if (pftrVariant.value === 'variant_4' && !inPromotionCatalogue.value && offers.length >= 4) {
				rowPosition = 3;
			}
			// EXP_PROMOTED_FREE_TRIAL_REDESIGN
			const freeTrialRow = { ...promotionOffer.value, rowType: 'freeTrial' } as BuyboxRegularOffer;
			offers.splice(Math.min(rowPosition, offers.length), 0, freeTrialRow);
		}
	}

	if (hasActiveJwtvCampaign.value) {
		// if the title has a JWTV offer, only show the JWTV offer, otherwise show no offers (JWTV is the only offer that should be shown during the campaign)
		const hasJWTVOffer = props.allOffers.some(offer => offer.package.shortName === 'jwt');
		if (hasJWTVOffer) {
			const jwtvOffer = offers.find(offer => offer.package.shortName === 'jwt');
			if (jwtvOffer) {
				offers = [jwtvOffer];
			} else {
				offers = [];
			}
		}
	}

	return offers.filter(Boolean);
});

/**
 * ALL: Promotions shown after NoOffers component instead (line 89)
 * PHYSICAL: Physical shouldn't show streaming promotions
 * CINEMA: Theater shouldn't show streaming promotions
 */
const showTopPositionPromotionPlacement = computed(
	() =>
		showNoOffers.value &&
		props.filter !== BuyBoxFilterOptions.ALL &&
		props.filter !== BuyBoxFilterOptions.PHYSICAL &&
		props.filter !== BuyBoxFilterOptions.CINEMA
);

/**
 * Filter exclusions:
 * PHYSICAL: Disc tab shouldn't render anything unless there are actual physcial media offers
 * CINEMA: same as above
 *
 * Special handling:
 * FREE filter: Only show if not in promotion catalogue (promotions show instead)
 * Other filters: Only show if no upcoming releases (upcoming releases show instead)
 */
const showTopPositionNoOffers = computed(
	() =>
		props.filter !== BuyBoxFilterOptions.PHYSICAL &&
		props.filter !== BuyBoxFilterOptions.CINEMA &&
		(props.filter === BuyBoxFilterOptions.FREE
			? showNoOffers.value && !inPromotionCatalogue.value
			: showNoOffers.value && props.upcomingReleases.length === 0)
);

// Split offers into visible and hidden for correct ordering
const visibleOffers = computed(() => {
	if (props.filter !== BuyBoxFilterOptions.ALL || !props.hasVODOffers) {
		return _allOffers.value;
	}
	// Filter out cinema and physical offers
	return _allOffers.value.filter(offer => {
		if (offer.monetizationType === 'CINEMA') return false;
		if (offer.presentationType && ['DVD', 'BLURAY', 'BLURAY_4K'].includes(offer.presentationType)) return false;
		return true;
	});
});

const hiddenOffers = computed(() => {
	if (props.filter !== BuyBoxFilterOptions.ALL || !props.hasVODOffers) {
		return [];
	}
	// Only cinema and physical offers
	return _allOffers.value.filter(offer => {
		if (offer.monetizationType === 'CINEMA') return true;
		if (offer.presentationType && ['DVD', 'BLURAY', 'BLURAY_4K'].includes(offer.presentationType)) return true;
		return false;
	});
});

return { __sfc: true,ListUpcoming, ListOffer, NoOffers, NotifyMe, PromotionPlacement, props, language, country, isLoggedIn, promotionOffer, inPromotionCatalogue, catalogueItem, buyRentExpVariant, pftrVariant, showFreeTrial, hasFreeTrialOffers, showNoOffers, showNotifyMeNew, hasOnlyUpcomingOffers, qualityTvTrackingContexts, isFreeTrialPositionFeature, isOverProviderTitle, includeTVODQTVonRentOrBuy, hasActiveJwtvCampaign, _allOffers, showTopPositionPromotionPlacement, showTopPositionNoOffers, visibleOffers, hiddenOffers }
}

})